/** View models and helpers of the DC detail (values come from the API). */
import type { DerivedDC } from '@/api/distributionCenters'
import type { SkuType } from './sku-model'

/** Below this, a quantity or percent difference is floating-point noise. */
export const DC_TOLERANCE = 1e-7

const unitsFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
})
export const dcUnits = (value: number) =>
  unitsFormatter.format(Math.abs(value) < DC_TOLERANCE ? 0 : value)
export const dcDelta = (value: number) =>
  `${value > DC_TOLERANCE ? '+' : ''}${dcUnits(value)}`

const costFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})
/** A PO cost, whole dollars. */
export const dcCost = (value: number) =>
  costFormatter.format(Math.abs(value) < 0.5 ? 0 : value)

/** DC id -> percent (0-100). */
export type DcSplit = Record<string, number>

/** Integer hundredths ensure that an equal split sums to exactly 100%. */
export function equalDcSplit(centers: DerivedDC[]): DcSplit {
  return Object.fromEntries(
    centers.map((dc, index) => [
      dc.id,
      (Math.floor(10000 / centers.length) +
        (index < 10000 % centers.length ? 1 : 0)) /
        100,
    ])
  )
}

interface DcLineInfo {
  id: string
  sku: string
  description: string
  type: SkuType
  vendor: string
}

/** The store model a line belongs to (lines without one are grouped together). */
interface DcStoreModel {
  id: string
  name: string
}

/** The vendor a line is bought from (``key`` '' = no vendor). */
interface DcVendor {
  key: string
  name: string
}

interface DcCell {
  percent: number
  raw: number
  adjusted: number
  delta: number
  /** PO cost of the adjusted units (a Child's PIAB units are on the parent's PO); null: unknown. */
  poCost?: number | null
}

/** One SKU's DC allocation in one wave week. */
export interface DcRow {
  id: string
  line: DcLineInfo
  waveQty: number
  distributionQty: number
  moqDelta: number
  /** Pack multiple each DC share rounds up to, and the line's MOQ. */
  logistics: { multiple: number; moq: number }
  /** The wave is below the line's MOQ: flagged, never raised. */
  belowMoq?: boolean
  /** Core routing: conveyable ships to one DC, not conveyable is split (null: unknown). */
  conveyable?: 'Yes' | 'No' | null
  /** Conveyable unknown (no case dimensions), so split across the line's DCs. */
  routingDefaulted?: boolean
  /** No store model: planned on the AP's Future Store Count, split by all stores per DC. */
  noStoreModel?: boolean
  /** The planner set this wave's DC split (not the default routing). */
  plannerSplit?: boolean
  /** By DC id. */
  allocations: Record<string, DcCell>
  percent: number
  raw: number
  adjusted: number
  delta: number
  balance: number
  balanced: boolean
  sourceNeedsReview: boolean
  /** Other visible waves where this SKU has units (copy targets). */
  otherWeekIds: string[]
  storeModel: DcStoreModel | null
  vendor?: DcVendor
  /** Captured unit cost (null: none). */
  unitCost?: number | null
  /** Sum of the cells' PO cost (null: none known). */
  poCost?: number | null
}

export interface DcTotals {
  waveQty: number
  raw: number
  adjusted: number
  delta: number
  balance: number
  excess: number
  excessCount: number
  excessPercent: number
  centers: (DerivedDC & { raw: number; adjusted: number; cost?: number })[]
  /** PO cost (Parent packs included: they carry the PIAB contents and the display). */
  cost?: number
  /** Lines with units but no PO cost (no unit cost, or a Child without PIAB contents). */
  costMissing?: number
}

/** A DC as the planner reads it: its code, then its name ("DC-EAST (East DC)"). */
export const dcLabel = (dc: { code: string; name?: string | null }) =>
  dc.name && dc.name !== dc.code ? `${dc.code} (${dc.name})` : dc.code

/** A row's units at a DC without a known PO cost. */
const missingCost = (row: DcRow) =>
  Object.values(row.allocations).some(
    cell => cell.adjusted > DC_TOLERANCE && cell.poCost == null
  )

/** Totals of a filtered set of rows: Parent quantities are excluded from unit totals. */
export function aggregateDcRows(rows: DcRow[], centers: DerivedDC[]): DcTotals {
  const unitRows = rows.filter(row => row.line.type !== 'Parent')
  const waveQty = unitRows.reduce((sum, row) => sum + row.waveQty, 0)
  const raw = unitRows.reduce((sum, row) => sum + row.raw, 0)
  const adjusted = unitRows.reduce((sum, row) => sum + row.adjusted, 0)
  // Do not let an underallocated SKU hide another SKU's excess units.
  const excessRows = unitRows.filter(row => row.balance > DC_TOLERANCE)
  const excess = excessRows.reduce((sum, row) => sum + row.balance, 0)
  return {
    waveQty,
    raw,
    adjusted,
    delta: adjusted - raw,
    balance: adjusted - waveQty,
    excess,
    excessCount: excessRows.length,
    excessPercent: waveQty > 0 ? (excess / waveQty) * 100 : 0,
    // Each line is served by its own DCs: a missing allocation counts as 0.
    centers: centers.map(dc => ({
      ...dc,
      raw: unitRows.reduce(
        (sum, row) => sum + (row.allocations[dc.id]?.raw ?? 0),
        0
      ),
      adjusted: unitRows.reduce(
        (sum, row) => sum + (row.allocations[dc.id]?.adjusted ?? 0),
        0
      ),
      // Every line, Parent included: its packs are on the PO.
      cost: rows.reduce(
        (sum, row) => sum + (row.allocations[dc.id]?.poCost ?? 0),
        0
      ),
    })),
    cost: rows.reduce(
      (sum, row) =>
        sum +
        Object.values(row.allocations).reduce(
          (total, cell) => total + (cell.poCost ?? 0),
          0
        ),
      0
    ),
    costMissing: rows.filter(missingCost).length,
  }
}

export type DcRowFilter = 'all' | 'split' | 'source' | 'adjusted' | 'over'

export const DC_ROW_FILTERS: { value: DcRowFilter; label: string }[] = [
  { value: 'all', label: 'All lines' },
  { value: 'split', label: 'Split issues' },
  { value: 'source', label: 'Wave allocation issues' },
  { value: 'adjusted', label: 'Logistics adjustments' },
  { value: 'over', label: 'Excess units' },
]

/** Rows matching a free-text search (SKU, description, type, vendor) and a filter. */
export function filterDcRows(
  rows: DcRow[],
  search: string,
  filter: DcRowFilter
): DcRow[] {
  const term = search.trim().toLowerCase()
  return rows.filter(row => {
    const { sku, description, type, vendor } = row.line
    if (
      term &&
      !`${sku} ${description} ${type} ${vendor}`.toLowerCase().includes(term)
    )
      return false
    switch (filter) {
      case 'split':
        return !row.balanced
      case 'source':
        return row.sourceNeedsReview
      case 'adjusted':
        return row.moqDelta > 0 || Math.abs(row.delta) > DC_TOLERANCE
      case 'over':
        return row.line.type !== 'Parent' && row.balance > DC_TOLERANCE
      default:
        return true
    }
  })
}

export const NO_STORE_MODEL = 'No store model'
export const NO_VENDOR = 'No vendor'

/** The lines of one store model or vendor ('' key: lines without one). */
interface DcRowGroup {
  key: string
  name: string
  rows: DcRow[]
}

/** A vendor's lines, by store model. */
interface DcVendorGroup extends DcRowGroup {
  models: DcRowGroup[]
}

/** Rows grouped by key: by name, the lines without one ('') last; rows keep their order. */
function groupRows(
  rows: DcRow[],
  keyOf: (row: DcRow) => string,
  nameOf: (row: DcRow) => string
): DcRowGroup[] {
  const groups = new Map<string, DcRowGroup>()
  for (const row of rows) {
    const key = keyOf(row)
    let group = groups.get(key)
    if (!group) {
      group = { key, name: nameOf(row), rows: [] }
      groups.set(key, group)
    }
    group.rows.push(row)
  }
  return [...groups.values()].sort((a, b) =>
    a.key === b.key
      ? 0
      : a.key === ''
        ? 1
        : b.key === ''
          ? -1
          : a.name.localeCompare(b.name) || a.key.localeCompare(b.key)
  )
}

/** Rows grouped by store model: by name, lines without one last; rows keep their order. */
export const groupDcRows = (rows: DcRow[]) =>
  groupRows(
    rows,
    row => row.storeModel?.id ?? '',
    row => row.storeModel?.name || NO_STORE_MODEL
  )

/** Collapse keys: a vendor, and a store model within its vendor. */
export const vendorKey = (vendor: string) => `v:${vendor}`
export const vendorModelKey = (vendor: string, model: string) =>
  `v:${vendor}|m:${model}`

/** Rows grouped by vendor, then store model, both ordered as ``groupDcRows`` orders. */
export function groupDcRowsByVendor(rows: DcRow[]): DcVendorGroup[] {
  return groupRows(
    rows,
    row => row.vendor?.key ?? '',
    row => row.vendor?.name || NO_VENDOR
  ).map(vendor => ({ ...vendor, models: groupDcRows(vendor.rows) }))
}

/** A store model's part of one page. */
interface DcGroupSection {
  group: DcRowGroup
  /** The group's lines on this page (none while it is collapsed). */
  rows: DcRow[]
  /** The group started on an earlier page. */
  continued: boolean
}

/** A vendor's part of one page: its store models' parts. */
export interface DcVendorSection {
  vendor: DcVendorGroup
  models: DcGroupSection[]
  /** The vendor started on an earlier page. */
  continued: boolean
}

/**
 * One page of lines grouped by vendor and store model. Each line is one item; a
 * collapsed vendor or store model counts as a single item, so collapsing brings
 * later groups onto earlier pages. Groups that continue repeat their headers.
 */
export function pageDcVendors(
  vendors: DcVendorGroup[],
  collapsed: ReadonlySet<string>,
  page: number,
  pageSize: number
): { sections: DcVendorSection[]; pageCount: number; page: number } {
  interface Item {
    vendor: DcVendorGroup
    vendorIndex: number
    model: DcRowGroup | null
    row: DcRow | null
    rowIndex: number
  }
  const items = vendors.flatMap((vendor): Item[] => {
    if (collapsed.has(vendorKey(vendor.key)))
      return [{ vendor, vendorIndex: 0, model: null, row: null, rowIndex: 0 }]
    let vendorIndex = 0
    return vendor.models.flatMap((model): Item[] =>
      collapsed.has(vendorModelKey(vendor.key, model.key))
        ? [
            {
              vendor,
              vendorIndex: vendorIndex++,
              model,
              row: null,
              rowIndex: 0,
            },
          ]
        : model.rows.map((row, rowIndex) => ({
            vendor,
            vendorIndex: vendorIndex++,
            model,
            row,
            rowIndex,
          }))
    )
  })
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize))
  const current = Math.min(Math.max(page, 0), pageCount - 1)
  const sections: DcVendorSection[] = []
  for (const item of items.slice(
    current * pageSize,
    (current + 1) * pageSize
  )) {
    let section = sections[sections.length - 1]
    if (section?.vendor !== item.vendor) {
      section = {
        vendor: item.vendor,
        models: [],
        continued: item.vendorIndex > 0,
      }
      sections.push(section)
    }
    if (!item.model) continue
    let model = section.models[section.models.length - 1]
    if (model?.group !== item.model) {
      model = { group: item.model, rows: [], continued: item.rowIndex > 0 }
      section.models.push(model)
    }
    if (item.row) model.rows.push(item.row)
  }
  return { sections, pageCount, page: current }
}
